import type { AuthFlag, Identity } from '../types/auth'
import { digest } from '../utils/auth'

/**
 * Terms and privacy are versioned so an acceptance recorded today can never be
 * read as acceptance of a future version.
 */
export const TERMS_VERSION = '2026-03'
export const PRIVACY_VERSION = '2026-03'

const SALT = 'seedsalt01'

/**
 * Development identities only. These exist so the sign-in, recovery, role and
 * status journeys can be exercised end to end; every one is flagged `seeded`
 * and the sign-in screen says so. Production must ship with none of these.
 */
export const seededIdentities: Identity[] = [
  {
    /* The account the rest of the product is already built around. */
    id: 'CAMP-8H2K41QD',
    username: 'amarachi',
    usernameKey: 'amarachi',
    usernameChangedAt: '2025-09-02T09:14:00Z',
    usernameHistory: [],
    displayName: 'Amarachi Obi',
    email: 'amarachi.obi@campuzon.com',
    emailState: 'verified',
    phone: '+234 806 555 0142',
    phoneState: 'verified',
    passwordHash: digest('CampusRain2026', SALT),
    passwordSalt: SALT,
    passwordUpdatedAt: '2026-01-11T18:02:00Z',
    status: 'active',
    statusReason: null,
    roles: ['student', 'campus_representative'],
    activeRole: 'student',
    countryCode: 'NG',
    institutionId: 'inst-unilag',
    campusId: 'campus-akoka',
    programme: 'BSc Computer Science',
    level: '300 level',
    interests: ['Accommodation', 'Marketplace', 'Gigs', 'Events'],
    photoInitials: 'AO',
    createdAt: '2025-09-02T09:14:00Z',
    lastLoginAt: '2026-08-13T07:41:00Z',
    consent: {
      termsVersion: '2025-08',
      privacyVersion: '2025-08',
      acceptedAt: '2025-09-02T09:14:00Z',
      marketingOptIn: false,
    },
    onboarding: {
      usernameConfirmed: true,
      photoAdded: true,
      campusSelected: true,
      programmeAdded: true,
      interestsAdded: true,
      notificationsSet: true,
      dismissed: false,
    },
    seeded: true,
  },
  {
    /* Registered but never verified — exercises the pending-verification gate. */
    id: 'CAMP-2P9WQ7XT',
    username: 'tunde.a',
    usernameKey: 'tunde.a',
    usernameChangedAt: null,
    usernameHistory: [],
    displayName: 'Tunde Adeyemi',
    email: 'tunde.adeyemi@campuzon.com',
    emailState: 'unverified',
    phone: null,
    phoneState: 'unverified',
    passwordHash: digest('OpenGateway77', SALT),
    passwordSalt: SALT,
    passwordUpdatedAt: '2026-08-10T10:20:00Z',
    status: 'pending_verification',
    statusReason: null,
    roles: ['student'],
    activeRole: 'student',
    countryCode: 'NG',
    institutionId: null,
    campusId: null,
    programme: null,
    level: null,
    interests: [],
    photoInitials: 'TA',
    createdAt: '2026-08-10T10:20:00Z',
    lastLoginAt: null,
    consent: {
      termsVersion: TERMS_VERSION,
      privacyVersion: PRIVACY_VERSION,
      acceptedAt: '2026-08-10T10:20:00Z',
      marketingOptIn: true,
    },
    onboarding: {
      usernameConfirmed: true,
      photoAdded: false,
      campusSelected: false,
      programmeAdded: false,
      interestsAdded: false,
      notificationsSet: false,
      dismissed: false,
    },
    seeded: true,
  },
  {
    /* Suspended — exercises the refusal path that must not leak the reason detail. */
    id: 'CAMP-5D1LM3BV',
    username: 'kelechi.n',
    usernameKey: 'kelechi.n',
    usernameChangedAt: null,
    usernameHistory: [],
    displayName: 'Kelechi Nwosu',
    email: 'kelechi.nwosu@campuzon.com',
    emailState: 'verified',
    phone: '+234 803 555 0198',
    phoneState: 'verified',
    passwordHash: digest('LagoonWalk401', SALT),
    passwordSalt: SALT,
    passwordUpdatedAt: '2026-04-02T08:00:00Z',
    status: 'suspended',
    statusReason: 'Account suspended pending a trust and safety review.',
    roles: ['student', 'service_provider'],
    activeRole: 'student',
    countryCode: 'NG',
    institutionId: 'inst-futa',
    campusId: 'campus-futa-main',
    programme: 'BEng Mechanical Engineering',
    level: '400 level',
    interests: ['Gigs', 'Library'],
    photoInitials: 'KN',
    createdAt: '2025-11-19T14:05:00Z',
    lastLoginAt: '2026-06-30T12:10:00Z',
    consent: {
      termsVersion: '2025-08',
      privacyVersion: '2025-08',
      acceptedAt: '2025-11-19T14:05:00Z',
      marketingOptIn: false,
    },
    onboarding: {
      usernameConfirmed: true,
      photoAdded: true,
      campusSelected: true,
      programmeAdded: true,
      interestsAdded: true,
      notificationsSet: true,
      dismissed: false,
    },
    seeded: true,
  },
]

/**
 * Authentication methods and public sections are configuration. A provider that
 * has not been configured stays off rather than presenting a button that fails.
 */
export const authFlags: AuthFlag[] = [
  {
    key: 'registration',
    label: 'Account creation',
    enabled: true,
    note: 'Open. Turning this off shows a closed-registration notice instead of the form.',
  },
  {
    key: 'email_auth',
    label: 'Email and password',
    enabled: true,
    note: 'The configured primary method.',
  },
  {
    key: 'phone_auth',
    label: 'Phone and password',
    enabled: false,
    note: 'Requires an SMS provider. Phone can still be verified as a second channel.',
  },
  {
    key: 'phone_verification',
    label: 'Phone verification',
    enabled: true,
    note: 'Optional during onboarding. Codes are one-time, expiring and attempt-limited.',
  },
  {
    key: 'oauth_google',
    label: 'Continue with Google',
    enabled: false,
    note: 'No client credentials configured, so the button is not shown at all.',
  },
  {
    key: 'passkeys',
    label: 'Passkeys',
    enabled: false,
    note: 'Architected as an additional AuthIdentity; not yet enabled.',
  },
  {
    key: 'username_system',
    label: 'Campuzon usernames',
    enabled: true,
    note: 'Reserved-name protection and uniqueness enforced server-side.',
  },
  {
    key: 'public_campus_pages',
    label: 'Public campus pages',
    enabled: true,
    note: 'Only campuses marked published appear publicly.',
  },
  {
    key: 'global_search',
    label: 'Public search',
    enabled: false,
    note: 'Search is available to signed-in people; the public surface is not enabled yet.',
  },
]

/**
 * What a visitor can reach without an account, and what needs one. `open`
 * reflects the ACTUAL route guards — nothing is advertised as browsable that
 * would bounce you to sign-in.
 */
export const publicCapabilities = [
  {
    title: 'Accommodation',
    description:
      'Verified and unverified student housing by campus, area, price and room type, with map search and roommate matching.',
    href: '/housing',
    open: false,
    gate: 'Needs an account — listings include contact details and applications.',
  },
  {
    title: 'Marketplace',
    description:
      'Books, study resources, services and mentorship from providers around your campus, with escrow on every order.',
    href: '/marketplace',
    open: false,
    gate: 'Needs an account to order. Fees are shown before you pay.',
  },
  {
    title: 'Academic services',
    description:
      'Which service each institution officially provides, what it needs, what it costs and who may legitimately assist you.',
    href: '/services',
    open: false,
    gate: 'Needs an account — requests carry your academic details.',
  },
  {
    title: 'Gigs and opportunities',
    description: 'Internships, graduate roles and campus work from verified employers.',
    href: '/opportunities',
    open: false,
    gate: 'Needs an account and a career profile to apply.',
  },
  {
    title: 'Library',
    description: 'Licensed textbooks, lecture material and courses you can rent or buy.',
    href: '/marketplace',
    open: false,
    gate: 'Reading a licensed copy needs an entitlement on your account.',
  },
  {
    title: 'Campus community',
    description: 'Events, groups and people at your institution.',
    href: '/community',
    open: false,
    gate: 'Community is only visible to signed-in members of a campus.',
  },
  {
    title: 'CampaGift',
    description: 'Give value someone can use with participating campus merchants.',
    href: '/money/campagift',
    open: false,
    gate: 'Needs an account. Real-money issuance is awaiting licensing.',
  },
  {
    title: 'Campus representative',
    description:
      'Apply to work as an authorised Campuzon field operator on your campus — verify housing, onboard businesses, build campus intelligence.',
    href: '/network',
    open: true,
    gate: 'Open to visitors. Applying does not need an account.',
  },
]
